import { action, computed, makeObservable, observable, runInAction } from 'mobx'

import { adaptSubscriptionPlan } from 'shared/adapters/subscriptions'
import { getSubscriptionPlans } from 'shared/api/subscriptions'
import { PLAN_YEARLY, PLAN_MONTHLY } from 'shared/constants/settings'

import { Messages } from 'retailer/pages/ManagePlanPage/constants'
import {
  RetailerSubscription,
  SubscriptionPlanStore,
  SubscriptionPlanCancelStore,
  SubscriptionPlanChangeStore, RetailerExceedsProductLimitStore,
} from 'retailer/stores'

const ManagePlanState = {
  IDLE: 'idle',
  LOADING: 'loading',
  LOADED: 'loaded',
  ERROR: 'error',
}

class ManagePlanStore {
  @observable state = ManagePlanState.IDLE
  @observable errorMessage = undefined
  @observable retailerSubscription = new RetailerSubscription()
  @observable subscriptionPlans = []
  @observable subscriptionPeriod = PLAN_YEARLY
  subscriptionPeriodsAvailable = [
    [PLAN_YEARLY, 'Yearly'],
    [PLAN_MONTHLY, 'Monthly']
  ]

  @observable planCancelStore
  @observable planChangeStore
  @observable retailerExceedsProductLimitStore

  constructor () {
    makeObservable(this)
  }

  @computed get wixError () {
    return this.subscriptionPlans.some(plan => !!plan.wixCheckoutUrl.error)
  }

  @computed get wixErrorCodes () {
    return this.subscriptionPlans.filter(plan => !!plan.wixCheckoutUrl.error).map(plan => plan.wixCheckoutUrl.error)
  }

  @computed get plansForPeriod () {
    return this.subscriptionPlans.filter(plan => !plan.period || plan.period === this.subscriptionPeriod)
  }

  @action.bound
  setError (message) {
    this.state = ManagePlanState.ERROR
    this.errorMessage = message
  }

  @action.bound
  setMissingRecurlyTokenError () {
    this.setError(Messages.error.generic)
  }

  /**
   * Load available plans and refresh user's subscription.
   * @returns {Promise<(AxiosResponse<any>|void)[]>}
   */
  @action.bound
  reload () {
    const promises = [
      getSubscriptionPlans().then(response => {
        runInAction(() => {
          this.subscriptionPlans = response.data.results.map(
            plan => new SubscriptionPlanStore(adaptSubscriptionPlan(plan))
          )
        })
      }),
      this.retailerSubscription.fetch()
    ]
    return Promise.all(promises)
  }

  /**
   * Uses reload with indicating loading state. Used for initial data fetch.
   * @returns {Promise<(AxiosResponse<*>|void)[]>}
   */
  @action.bound
  async fetch () {
    this.errorMessage = null
    this.state = ManagePlanState.LOADING
    try {
      await this.reload()
      runInAction(() => {
        this.state = ManagePlanState.LOADED
      })
      return true
    } catch {
      this.setError(Messages.error.generic)
      return false
    }
  }

  @action.bound
  changeSubscriptionPeriod (value) {
    this.subscriptionPeriod = value
  }

  @action.bound
  selectPlanForChange (plan) {
    this.planChangeStore = new SubscriptionPlanChangeStore(this, plan)
  }

  @action.bound
  resetPlanForChange () {
    this.planChangeStore = null
  }

  @action.bound
  startPlanCancellation () {
    this.planCancelStore = new SubscriptionPlanCancelStore(this)
  }

  @action.bound
  resetPlanCancellation () {
    this.planCancelStore = null
  }

  @action.bound
  setRetailerExceedsProductLimitStore (plan) {
    this.retailerExceedsProductLimitStore = new RetailerExceedsProductLimitStore(this, plan)
  }

  @action.bound
  resetRetailerExceedsProductLimitStore () {
    this.retailerExceedsProductLimitStore = null
  }

  @action.bound
  reset () {
    this.retailerSubscription = new RetailerSubscription()
    this.subscriptionPlans = []
    this.subscriptionPeriod = PLAN_YEARLY
    this.planCancelStore = null
    this.planChangeStore = null
    this.retailerExceedsProductLimitStore = null
    this.errorMessage = undefined
    this.state = ManagePlanState.IDLE
  }
}

export { ManagePlanState }

export default ManagePlanStore
